/**
 * securityHelper.js
 * Created by michaelbray on 2/14/18.
 */

import moment from 'moment';
import { isNil } from 'lodash';

import LocalStorage from './storage/LocalStorage';
import CookieStorage from './storage/CookieStorage';
import SessionStorage from './storage/SessionStorage';

let _isMobileApp = null;
let _isMobileNativeCartEnabled = null;

export function isLoggedIn() {
    const acct = LocalStorage.getItem('recaccount');
    if (acct == null) {
        return false;
    }

    const recAccount = JSON.parse(acct);

    const exp = recAccount.expiration;
    if (!recAccount.is_guest && moment.utc(exp).isAfter()) {
        return true;
    }

    if (moment.utc(exp).isBefore()) {
        LocalStorage.removeItem('recaccount');
    }

    return false;
}

export function isInternalUserLoggedIn() {
    const account = LocalStorage.getItem('internalaccount') !== null;

    if (!account) {
        return false
    }

    return true;
}

export function isGuest() {
    const acct = LocalStorage.getItem('recaccount');
    if (acct == null) {
        return false;
    }

    const recAccount = JSON.parse(acct);

    if (moment.utc(recAccount.expiration).isBefore()) {
        LocalStorage.removeItem('recaccount');
        return false;
    }

    return recAccount.is_guest;
}

export function isExternalCSRLoggedIn() {
    const acct = LocalStorage.getItem('csraccount');
    if (acct == null) {
        return false;
    }

    const csrAccount = JSON.parse(acct);

    if (moment.utc(csrAccount.expiration).isBefore()) {
        LocalStorage.removeItem('csraccount');
        return false;
    }

    return true;
}

// checks whether this user session is for mobile app browser which will
// trigger changes to global navigation
export function isMobileApp() {
    if (_isMobileApp === null) {
        // initialize cached value of the _isMobileApp flag by checking for
        // mobile app cookie, session storage flag, or query param flag
        const authCookie = new CookieStorage().readCookie('X-Mobile-Auth');
        const storageFlag = SessionStorage.getItem('isMobileApp');
        const hasMobileQueryFlag = window.location.search.indexOf("src=mobileApp") >= 0;

        if (authCookie !== null || storageFlag === 'true' || hasMobileQueryFlag) {
            SessionStorage.setItem('isMobileApp', 'true');
            _isMobileApp = true;
        }
        else {
            _isMobileApp = false;
        }
    }
    return _isMobileApp;
}

// checks whether this user session supports mobile native cart for order and payment integration
// in to trigger changes to global navigation
export function isMobileNativeCartEnabled() {
    if (_isMobileNativeCartEnabled === null) {
        // initialize cached value of the _isMobileNativeCartEnabled flag by checking for
        // mobile app cookie, session storage flag, or query param flag
        const mCookie = new CookieStorage().readCookie('X-Mobile-Native-Cart');
        const storageFlag = SessionStorage.getItem('isMobileNativeCartEnabled');
        const hasMobileQueryFlag = window.location.search.indexOf("mobileNativeCart=true") >= 0;

        if (mCookie !== null || storageFlag === 'true' || hasMobileQueryFlag) {
            SessionStorage.setItem('isMobileNativeCartEnabled', 'true');
            _isMobileNativeCartEnabled = true;
        }
        else {
            _isMobileNativeCartEnabled = false;
        }
    }
    return _isMobileNativeCartEnabled;
}

// returns mobile native recaptcha token passed by native mobile apps
export function getMobileNativeRecaptchaToken() {
    if (isMobileApp()) {
        const recaptchaCookie = new CookieStorage().readCookie('X-Mobile-Recaptcha');
        if (recaptchaCookie !== null) {
            return recaptchaCookie;
        }
    }
    return null;
}

// returns mobile geofencing location token passed by native mobile apps
export function getMobileNativeLocation() {
    if (isMobileApp()) {
        const geoToken = new CookieStorage().readCookie('X-Mobile-Location');
        if (geoToken !== null) {
            return geoToken;
        }
    }
    return null;
}


export function getRecAccount() {
    if (isLoggedIn()) {
        return JSON.parse(LocalStorage.getItem('recaccount'));
    }

    return {};
}

export function getGuestAccount() {
    if (isGuest()) {
        return JSON.parse(LocalStorage.getItem('recaccount'));
    }

    return {};
}

export function getExternalCSRAccount() {
    if (isExternalCSRLoggedIn()) {
        return JSON.parse(LocalStorage.getItem('csraccount'));
    }

    return {};
}

export function getAccount() {
    if (isLoggedIn()) {
        return getRecAccount().account;
    }

    return {};
}

export function getInternalAccountId() {
    const internalAccount = JSON.parse(LocalStorage.getItem('internalaccount'));
    if (isNil(internalAccount) || isNil(internalAccount.user)) {
        return '';
    }

    return internalAccount?.user?.user_id;
}

export function isCommercialAccount() {
    const account = getAccount();

    if (Object.keys(account).length > 0 && account.hasOwnProperty('is_commercial_account')) {
        return account.is_commercial_account;
    }

    return false;
}

export function getAuthHeader() {
    if (isLoggedIn()) {
        return `Bearer ${getRecAccount().access_token}`;
    }

    if (isGuest()) {
        return `Bearer ${getGuestAccount().access_token}`;
    }

    return '';
}

export function getInternalAuthHeader() {
    const internalAccount = JSON.parse(LocalStorage.getItem('internalaccount'));
    if (internalAccount === null) {
        return '';
    }
    return `Bearer ${internalAccount.access_token}`;
}

export function getExternalCSRAuthHeader() {
    if (isExternalCSRLoggedIn()) {
        return `Bearer ${getExternalCSRAccount().access_token}`;
    }
    return '';
}
